/**
 * Haversine formula — calculates the great-circle distance between two GPS points.
 * @param {number} lat1 - Latitude of point 1 (degrees)
 * @param {number} lon1 - Longitude of point 1 (degrees)
 * @param {number} lat2 - Latitude of point 2 (degrees)
 * @param {number} lon2 - Longitude of point 2 (degrees)
 * @returns {number} Distance in metres
 */
export function haversineDistance(lat1, lon1, lat2, lon2) {
  const R = 6371000 // Earth radius in metres
  const toRad = (deg) => (deg * Math.PI) / 180

  const dLat = toRad(lat2 - lat1)
  const dLon = toRad(lon2 - lon1)

  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(toRad(lat1)) *
      Math.cos(toRad(lat2)) *
      Math.sin(dLon / 2) *
      Math.sin(dLon / 2)

  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a))
  return R * c
}

/**
 * Checks whether an employee's location is within the org's geofence.
 * @param {number} empLat
 * @param {number} empLng
 * @param {number} orgLat
 * @param {number} orgLng
 * @param {number} radiusMetres
 * @returns {boolean}
 */
export function isWithinGeofence(empLat, empLng, orgLat, orgLng, radiusMetres) {
  const distance = haversineDistance(empLat, empLng, orgLat, orgLng)
  return distance <= radiusMetres
}

/**
 * Wraps the browser's Geolocation API in a promise.
 * @returns {Promise<{latitude: number, longitude: number}>}
 */
export function getCurrentPosition() {
  return new Promise((resolve, reject) => {
    if (!navigator.geolocation) {
      reject(new Error('GEOLOCATION_NOT_SUPPORTED'))
      return
    }

    navigator.geolocation.getCurrentPosition(
      (position) => {
        resolve({
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
        })
      },
      (error) => {
        switch (error.code) {
          case error.PERMISSION_DENIED:
            reject(new Error('PERMISSION_DENIED'))
            break
          case error.POSITION_UNAVAILABLE:
            reject(new Error('POSITION_UNAVAILABLE'))
            break
          case error.TIMEOUT:
            reject(new Error('TIMEOUT'))
            break
          default:
            reject(new Error('UNKNOWN_ERROR'))
        }
      },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 }
    )
  })
}
